import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Paper,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import { ClassId } from '../types';
import { verifyAccessCode } from '../services/api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SchoolIcon from '@mui/icons-material/School';

// 로컬 스토리지 키
const ADMIN_KEY = 'admin_logged_in';
const USER_LOGIN_KEY = 'user_logged_in';

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [userCode, setUserCode] = useState('');
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userClassId, setUserClassId] = useState<ClassId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loginTab, setLoginTab] = useState(0); // 0: 사용자, 1: 관리자
  const [selectedClass, setSelectedClass] = useState<ClassId | ''>('');
  const [showClassSelection, setShowClassSelection] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [aiRecordLoginOpen, setAiRecordLoginOpen] = useState(false);
  const [aiRecordForm, setAiRecordForm] = useState({
    email: '',
    password: '',
    name: '',
    isLogin: true // true: 로그인, false: 회원가입
  });

  // 컴포넌트 마운트 시 로컬 스토리지에서 사용자 로그인 상태 확인
  useEffect(() => {
    const userLoginStatus = localStorage.getItem(USER_LOGIN_KEY);
    if (userLoginStatus) {
      setIsUserLoggedIn(true);
      setUserClassId(userLoginStatus as ClassId);
    }
  }, []);

  const handleAdminOpen = () => {
    setLoginTab(1); // 관리자 탭으로 전환
    setAdminOpen(true);
  };
  const handleAdminClose = () => setAdminOpen(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setLoginTab(newValue);
  };

  const handleAdminLogin = () => {
    if (adminCode === 'bnvaitool') {
      localStorage.setItem(ADMIN_KEY, 'true');
      setAdminCode('');
      handleAdminClose();
      // 관리자 대시보드로 즉시 리다이렉트 (replace: true로 히스토리 스택에서 제거)
      navigate('/admin', { replace: true });
    } else {
      setError('잘못된 관리자 코드입니다');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleUserLogin = async () => {
    if (!selectedClass || !userCode) {
      setError('반과 코드를 모두 입력해주세요');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const response = await verifyAccessCode(userCode);
      if (response.success) {
        setIsUserLoggedIn(true);
        setUserClassId(selectedClass);
        localStorage.setItem(USER_LOGIN_KEY, selectedClass);
        setSuccess(`${selectedClass} 반 로그인 성공`);
        setTimeout(() => setSuccess(null), 3000);
        navigate('/welcome', { state: { classId: selectedClass } });
      } else {
        setError('잘못된 코드입니다');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      setError('로그인 처리 중 오류가 발생했습니다');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleUserLogout = () => {
    setIsUserLoggedIn(false);
    setUserClassId(null);
    localStorage.removeItem(USER_LOGIN_KEY);
    setSuccess('로그아웃 성공');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleClassChange = (event: SelectChangeEvent<string>) => {
    setSelectedClass(event.target.value as ClassId);
    setShowCodeInput(true);
  };

  const handleBackToClassSelection = () => {
    setSelectedClass('');
    setShowCodeInput(false);
  };

  const handleAiRecordLogin = () => {
    setAiRecordLoginOpen(true);
  };

  const handleAiRecordLoginClose = () => {
    setAiRecordLoginOpen(false);
    setAiRecordForm({
      email: '',
      password: '',
      name: '',
      isLogin: true
    });
  };

  const handleAiRecordFormChange = (field: string, value: string) => {
    setAiRecordForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAiRecordAuth = async () => {
    // TODO: 실제 인증 API 호출
    // 현재는 임시로 바로 페이지 이동
    if (aiRecordForm.isLogin) {
      // 로그인 처리
      if (aiRecordForm.email && aiRecordForm.password) {
        localStorage.setItem('ai_record_user', JSON.stringify({
          email: aiRecordForm.email,
          name: aiRecordForm.name || aiRecordForm.email
        }));
        handleAiRecordLoginClose();
        navigate('/ai-record');
      } else {
        setError('이메일과 비밀번호를 입력해주세요');
        setTimeout(() => setError(null), 3000);
      }
    } else {
      // 회원가입 처리
      if (aiRecordForm.email && aiRecordForm.password && aiRecordForm.name) {
        localStorage.setItem('ai_record_user', JSON.stringify({
          email: aiRecordForm.email,
          name: aiRecordForm.name
        }));
        handleAiRecordLoginClose();
        navigate('/ai-record');
      } else {
        setError('모든 필드를 입력해주세요');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const classes: ClassId[] = [
    'CLASS_01', 'CLASS_02', 'CLASS_03', 'CLASS_04', 'CLASS_05',
    'CLASS_06', 'CLASS_07', 'CLASS_08', 'CLASS_09', 'CLASS_10',
    'BNV_CREW'
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          zIndex: 0,
        },
        zIndex: 1,
      }}
    >
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            width: '100%', 
            maxWidth: 600,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            color: 'white',
            zIndex: 2,
            position: 'relative',
          }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2, 
            width: '100%', 
            maxWidth: 600,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            color: 'white',
            zIndex: 2,
            position: 'relative',
          }}
        >
          {success}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 600,
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          zIndex: 2,
          position: 'relative',
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          align="center" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            color: 'white',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            background: 'linear-gradient(45deg, #fff, #f0f0f0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          BNV AI TOOLS
        </Typography>

        <Divider sx={{ my: 2, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />

        {isUserLoggedIn ? (
          // 사용자 로그인 후 화면
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                {userClassId} 반 로그인
              </Typography>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleUserLogout}
                startIcon={<LogoutIcon />}
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                로그아웃
              </Button>
            </Box>

            <Typography variant="body1" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              {userClassId} 반에 로그인되었습니다.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/welcome', { state: { classId: userClassId } })}
              startIcon={<PersonIcon />}
              sx={{ 
                py: 1.5,
                background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '16px',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))',
                }
              }}
            >
              AI 도구 사용하기
            </Button>
          </Box>
        ) : (
          // 로그인 화면 (사용자/관리자 탭)
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={loginTab}
              onChange={handleTabChange}
              centered
              sx={{ 
                mb: 3,
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 'bold',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              }}
            >
              <Tab
                label="AI 도구 사용"
                icon={<PlayArrowIcon />}
                iconPosition="start"
                sx={{
                  background: loginTab === 0 ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  backdropFilter: loginTab === 0 ? 'blur(10px)' : 'none',
                  border: loginTab === 0 ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                  borderRadius: '12px',
                  mx: 1,
                }}
              />
              <Tab
                label="관리자 로그인"
                icon={<AdminPanelSettingsIcon />}
                iconPosition="start"
                sx={{
                  background: loginTab === 1 ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  backdropFilter: loginTab === 1 ? 'blur(10px)' : 'none',
                  border: loginTab === 1 ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                  borderRadius: '12px',
                  mx: 1,
                }}
              />
            </Tabs>

            {loginTab === 0 ? (
              // AI 도구 사용 탭
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
                  로그인 없이 AI 도구를 바로 사용하세요!
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3, textAlign: 'center' }}>
                  다양한 AI 도구와 생활기록부 작성 도구를 자유롭게 이용할 수 있습니다.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/tools')}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      py: 2,
                      background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.1))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      borderRadius: '16px',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.2))',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      }
                    }}
                  >
                    AI 도구 사용하기
                  </Button>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAiRecordLogin}
                    startIcon={<SchoolIcon />}
                    sx={{
                      py: 2,
                      background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.1))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(33, 150, 243, 0.3)',
                      borderRadius: '16px',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      '&:hover': {
                        background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(33, 150, 243, 0.2))',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      }
                    }}
                  >
                    AI 생기부 작성하기
                  </Button>
                </Box>
              </Box>
            ) : (
              // 관리자 로그인 탭
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 2 }}>
                  관리자 코드 입력
                </Typography>
                <TextField
                  fullWidth
                  label="관리자 코드"
                  type="password"
                  variant="outlined"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAdminLogin();
                    }
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAdminLogin}
                  startIcon={<AdminPanelSettingsIcon />}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '16px',
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))',
                    }
                  }}
                >
                  관리자 로그인
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* AI 생기부 로그인 모달 */}
      <Dialog 
        open={aiRecordLoginOpen} 
        onClose={handleAiRecordLoginClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center', pb: 1 }}>
          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI 생기부 {aiRecordForm.isLogin ? '로그인' : '회원가입'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Tabs
              value={aiRecordForm.isLogin ? 0 : 1}
              onChange={(e, value) => setAiRecordForm(prev => ({ ...prev, isLogin: value === 0 }))}
              centered
              sx={{
                mb: 3,
                '& .MuiTabs-indicator': {
                  backgroundColor: 'white',
                  height: 3,
                },
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 'bold',
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              }}
            >
              <Tab label="로그인" />
              <Tab label="회원가입" />
            </Tabs>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!aiRecordForm.isLogin && (
                <TextField
                  fullWidth
                  label="이름"
                  value={aiRecordForm.name}
                  onChange={(e) => handleAiRecordFormChange('name', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2196f3',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#2196f3',
                      },
                    },
                  }}
                />
              )}
              <TextField
                fullWidth
                label="이메일"
                type="email"
                value={aiRecordForm.email}
                onChange={(e) => handleAiRecordFormChange('email', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#2196f3',
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="비밀번호"
                type="password"
                value={aiRecordForm.password}
                onChange={(e) => handleAiRecordFormChange('password', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAiRecordAuth();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: '#2196f3',
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAiRecordLoginClose}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    background: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                취소
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAiRecordAuth}
                startIcon={<SchoolIcon />}
                sx={{
                  background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(33, 150, 243, 0.3)',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: 'bold',
                  '&:hover': {
                    background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.3), rgba(33, 150, 243, 0.2))',
                  }
                }}
              >
                {aiRecordForm.isLogin ? '로그인' : '회원가입'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MainPage; 